import classNames from "classnames";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import React from "react";
import AuthorMeta from "../components/draftbox-meta/AuthorMeta";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";
import PostCard from "../components/PostCard";
import { AllDraftboxPostCardData } from "../models/all-draftbox-post-description.model";
import { DraftboxAuthor } from "../models/draftbox-author-description.model";
import { PaginationContext } from "../models/pagination.model";

type AuthorTemplateProps = {
  data: {
    allDraftboxPost: AllDraftboxPostCardData;
    draftboxAuthor: DraftboxAuthor;
  };
  location: any;
  pageContext?: PaginationContext;
};

const AuthorTemplate: React.FC<AuthorTemplateProps> = ({
  data,
  location,
  pageContext,
}) => {
  const { allDraftboxPost, draftboxAuthor } = data;

  return (
    <Layout>
      <AuthorMeta author={draftboxAuthor} />
      <section
        className="text-center bg-cover bg-center relative overflow-hidden"
        style={{
          minHeight: "25vh",
        }}
      >
        <div className="absolute inset-0">
          {draftboxAuthor.coverImage?.childImageSharp && (
            <Img
              className="h-full"
              fluid={draftboxAuthor.coverImage.childImageSharp.fluid}
            />
          )}
          {draftboxAuthor.coverImage &&
            !draftboxAuthor.coverImage?.childImageSharp && (
              <img
                className="h-full w-full object-cover"
                src={draftboxAuthor.coverImage.publicURL}
              />
            )}
          {!draftboxAuthor.coverImage && pageContext.coverUrl && (
            <Img
              className="h-full"
              fluid={pageContext.coverUrl["childImageSharp"].fluid}
            />
          )}
        </div>
        <div className="relative flex items-center py-20 lg:py-32">
          <div
            className={classNames("absolute inset-0", {
              "bg-black opacity-70":
                draftboxAuthor.coverImage || pageContext.coverUrl,
              "bg-primaryActive":
                !draftboxAuthor.coverImage && !pageContext.coverUrl,
            })}
          />
          <div className="z-10 max-w-2xl mx-auto px-4">
            {draftboxAuthor.profileImage?.childImageSharp && (
              <div className="w-16 lg:w-24 mx-auto mb-4 rounded-full">
                <Img
                  className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-4 rounded-full"
                  fluid={draftboxAuthor.profileImage.childImageSharp.fixed}
                />
              </div>
            )}
            {draftboxAuthor.profileImage &&
              !draftboxAuthor.profileImage?.childImageSharp && (
                <div className="w-16 lg:w-24 mx-auto mb-4 rounded-full">
                  <img
                    className="w-16 h-16 lg:w-24 lg:h-24 mx-auto mb-4 rounded-full object-cover"
                    src={draftboxAuthor.profileImage.publicURL}
                  />
                </div>
              )}
            {!draftboxAuthor.profileImage && (
              <div className="w-16 h-16 lg:w-24 lg:h-24 mb-4 bg-white text-gray-500 rounded-full flex justify-center items-center mx-auto">
                <i className="far fa-user fa-lg"></i>
              </div>
            )}
            <span className="text-sm leading-tight font-sansNormal text-white opacity-70 uppercase">
              {draftboxAuthor.count.posts}{" "}
              {draftboxAuthor.count.posts > 1 ? "posts" : "post"}
            </span>
            <h1 className="my-4 text-2xl lg:text-3xl leading-tight font-sansBold text-white break-words">
              {draftboxAuthor.name}
            </h1>
            {/* <span className="text-lg font-semibold font-sans text-white">
              Regional Manager
            </span> */}
            {draftboxAuthor.bio && (
              <p
                className="dangerous-html text-lg lg:text-xl my-4 font-serifLight text-white opacity-85"
                dangerouslySetInnerHTML={{ __html: draftboxAuthor.bio }}
              ></p>
            )}
            {draftboxAuthor.socials?.length > 0 && (
              <div className="flex flex-wrap justify-center items-center space-x-6">
                {draftboxAuthor.socials.map((social, index) => {
                  return social.url.startsWith("/") ? (
                    <Link key={index} to={social.url}>
                      <i
                        className={`${social.icon[0]} fa-${social.icon[1]} text-white fa-lg`}
                      ></i>
                    </Link>
                  ) : (
                    <a
                      key={index}
                      rel="noreferrer noopener"
                      href={`${social.url}`}
                      target="_blank"
                      aria-label={social.label}
                    >
                      <i
                        className={`${social.icon[0]} fa-${social.icon[1]} text-white fa-lg`}
                      ></i>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="px-6 container mx-auto">
        <div className="flex justify-center flex-wrap -mx-4">
          {allDraftboxPost.edges.map(({ node }, i) => {
            return <PostCard post={node} index={i} key={i} />;
          })}
        </div>
      </section>
      <Pagination pageContext={pageContext} />
    </Layout>
  );
};

export default AuthorTemplate;

export const pageQuery = graphql`
  query DraftboxAuthorQuery($slug: String!, $limit: Int!, $skip: Int!) {
    draftboxAuthor(slug: { eq: $slug }) {
      ...DraftboxAuthorDetails
    }

    allDraftboxPost(
      sort: { order: DESC, fields: [publishedAt] }
      filter: { authors: { elemMatch: { slug: { eq: $slug } } } }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          ...DraftboxPostCardData
        }
      }
    }
  }
`;
